import React, { useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Download, Server, Database, Globe, Terminal, FileCode, Copy, Check, Package, Shield, RefreshCw, HardDrive, Cpu, MemoryStick } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminDeploymentPage = () => {
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success('Copiat în clipboard!');
    setTimeout(() => setCopiedSection(null), 3000);
  };

  const CodeBlock = ({ code, section, language = 'bash' }) => (
    <div className="relative">
      <pre className="bg-slate-950 p-4 rounded-lg overflow-x-auto text-sm text-green-400 font-mono whitespace-pre-wrap">
        <code>{code}</code>
      </pre>
      <button
        onClick={() => copyToClipboard(code, section)}
        className="absolute top-2 right-2 p-2 bg-slate-800 hover:bg-slate-700 rounded transition-colors"
        title="Copiază"
      >
        {copiedSection === section ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
      </button>
    </div>
  );

  // Script 1: Instalare sistem
  const installScript = `#!/bin/bash
# ===========================================
# RENTIASI.RO - Instalare Dependențe Linux
# Compatibil: Ubuntu 22.04 LTS / Debian 12
# ===========================================

set -e
echo "🚀 Începe instalarea dependențelor pentru RENTIASI.RO..."

# Actualizare sistem
sudo apt update && sudo apt upgrade -y

# Instalare utilitare de bază
sudo apt install -y curl wget git unzip software-properties-common apt-transport-https ca-certificates gnupg lsb-release

# Instalare Python 3.11
sudo apt install -y python3 python3-pip python3-venv python3-dev

# Instalare Node.js 20 LTS
curl -fsSL https://deb.nodesource.com/setup_20.x | sudo -E bash -
sudo apt install -y nodejs

# Instalare Yarn
sudo npm install -g yarn

# Instalare Nginx
sudo apt install -y nginx

# Instalare Certbot pentru SSL
sudo apt install -y certbot python3-certbot-nginx

# Instalare MongoDB 7.0
curl -fsSL https://www.mongodb.org/static/pgp/server-7.0.asc | \\
   sudo gpg -o /usr/share/keyrings/mongodb-server-7.0.gpg --dearmor

echo "deb [ arch=amd64,arm64 signed-by=/usr/share/keyrings/mongodb-server-7.0.gpg ] https://repo.mongodb.org/apt/ubuntu jammy/mongodb-org/7.0 multiverse" | \\
   sudo tee /etc/apt/sources.list.d/mongodb-org-7.0.list

sudo apt update
sudo apt install -y mongodb-org

# Pornire MongoDB
sudo systemctl start mongod
sudo systemctl enable mongod

# Verificare versiuni instalate
echo ""
echo "✅ Instalare completă! Versiuni:"
echo "   Python: $(python3 --version)"
echo "   Node.js: $(node --version)"
echo "   NPM: $(npm --version)"
echo "   Yarn: $(yarn --version)"
echo "   MongoDB: $(mongod --version | head -1)"
echo "   Nginx: $(nginx -v 2>&1)"`;

  // Script 2: Creare structură directoare
  const structureScript = `#!/bin/bash
# ===========================================
# Creare Structură Directoare
# ===========================================

# Creare director principal
sudo mkdir -p /var/www/rentiasi
sudo chown -R $USER:$USER /var/www/rentiasi

# Creare subdirectoare
cd /var/www/rentiasi
mkdir -p backend frontend scripts logs uploads

# Setare permisiuni
chmod 755 /var/www/rentiasi
chmod 755 /var/www/rentiasi/uploads

echo "✅ Structura directoarelor creată:"
echo "/var/www/rentiasi/"
echo "├── backend/"
echo "├── frontend/"
echo "├── scripts/"
echo "├── logs/"
echo "└── uploads/"`;

  // Script 3: Configurare Backend
  const backendScript = `#!/bin/bash
# ===========================================
# Configurare Backend Python/FastAPI
# ===========================================

cd /var/www/rentiasi

# Creare mediu virtual Python
python3 -m venv venv
source venv/bin/activate

# Actualizare pip
pip install --upgrade pip setuptools wheel

# IMPORTANT: Copiază fișierul server.py în /var/www/rentiasi/backend/
# Copiază requirements.txt în /var/www/rentiasi/backend/

# Instalare dependențe
cd backend
pip install -r requirements.txt

# Creare fișier .env
cat > .env << 'ENVFILE'
MONGO_URL="mongodb://localhost:27017"
DB_NAME="rentiasi"
JWT_SECRET_KEY="schimba-aceasta-cheie-secreta-cu-una-proprie"
ADMIN_DEFAULT_USERNAME=admin
ADMIN_DEFAULT_PASSWORD=parola-ta-sigura
CORS_ORIGINS="https://rentiasi.ro,https://www.rentiasi.ro"
PUNCH_SECRET_KEY="cheie-pontaj-secreta"
ENVFILE

echo "✅ Backend configurat!"
echo "⚠️  IMPORTANT: Modifică parolele din fișierul .env!"`;

  // Script 4: Configurare Frontend
  const frontendScript = `#!/bin/bash
# ===========================================
# Configurare Frontend React
# ===========================================

cd /var/www/rentiasi/frontend

# IMPORTANT: Copiază toate fișierele din frontend/ aici
# sau clonează din repository

# Instalare dependențe Node.js
yarn install

# Creare fișier .env pentru producție
cat > .env << 'ENVFILE'
REACT_APP_BACKEND_URL=https://rentiasi.ro
ENVFILE

# Compilare pentru producție
yarn build

echo "✅ Frontend compilat!"
echo "   Fișierele sunt în: /var/www/rentiasi/frontend/build/"`;

  // Configurare Nginx
  const nginxConfig = `# /etc/nginx/sites-available/rentiasi.ro
# Configurare Nginx pentru RENTIASI.RO

# Redirecționare HTTP -> HTTPS
server {
    listen 80;
    listen [::]:80;
    server_name rentiasi.ro www.rentiasi.ro;
    return 301 https://rentiasi.ro$request_uri;
}

# Redirecționare www -> non-www
server {
    listen 443 ssl http2;
    listen [::]:443 ssl http2;
    server_name www.rentiasi.ro;
    
    ssl_certificate /etc/letsencrypt/live/rentiasi.ro/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/rentiasi.ro/privkey.pem;
    
    return 301 https://rentiasi.ro$request_uri;
}

# Server principal
server {
    listen 443 ssl http2;
    listen [::]:443 ssl http2;
    server_name rentiasi.ro;

    # Certificate SSL (generate cu Certbot)
    ssl_certificate /etc/letsencrypt/live/rentiasi.ro/fullchain.pem;
    ssl_certificate_key /etc/letsencrypt/live/rentiasi.ro/privkey.pem;
    
    # Configurări SSL recomandate
    ssl_protocols TLSv1.2 TLSv1.3;
    ssl_ciphers ECDHE-ECDSA-AES128-GCM-SHA256:ECDHE-RSA-AES128-GCM-SHA256:ECDHE-ECDSA-AES256-GCM-SHA384:ECDHE-RSA-AES256-GCM-SHA384;
    ssl_prefer_server_ciphers off;
    ssl_session_cache shared:SSL:10m;
    ssl_session_timeout 1d;

    # Root pentru fișierele React compilate
    root /var/www/rentiasi/frontend/build;
    index index.html;

    # Limite upload fișiere (50MB)
    client_max_body_size 50M;

    # Proxy către Backend FastAPI - toate rutele /api/
    location /api/ {
        proxy_pass http://127.0.0.1:8001;
        proxy_http_version 1.1;
        proxy_set_header Upgrade $http_upgrade;
        proxy_set_header Connection 'upgrade';
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        proxy_set_header X-Forwarded-Proto $scheme;
        proxy_cache_bypass $http_upgrade;
        proxy_read_timeout 300s;
        proxy_connect_timeout 75s;
    }

    # Proxy pentru upload imagini
    location /upload/ {
        proxy_pass http://127.0.0.1:8001;
        proxy_http_version 1.1;
        proxy_set_header Host $host;
        proxy_set_header X-Real-IP $remote_addr;
        proxy_set_header X-Forwarded-For $proxy_add_x_forwarded_for;
        client_max_body_size 50M;
    }

    # Cache pentru fișiere statice
    location /static/ {
        expires 1y;
        add_header Cache-Control "public, immutable";
        access_log off;
    }

    # Cache pentru imagini
    location ~* \\.(jpg|jpeg|png|gif|ico|svg|webp)$ {
        expires 30d;
        add_header Cache-Control "public";
        access_log off;
    }

    # React Router - toate rutele servesc index.html
    location / {
        try_files $uri $uri/ /index.html;
    }

    # Security headers
    add_header X-Frame-Options "SAMEORIGIN" always;
    add_header X-Content-Type-Options "nosniff" always;
    add_header X-XSS-Protection "1; mode=block" always;
    add_header Referrer-Policy "strict-origin-when-cross-origin" always;

    # Loguri
    access_log /var/log/nginx/rentiasi.access.log;
    error_log /var/log/nginx/rentiasi.error.log;
}`;

  // Configurare Nginx script
  const nginxSetupScript = `#!/bin/bash
# ===========================================
# Instalare Configurare Nginx + SSL
# ===========================================

# Copiază configurarea
sudo cp nginx-rentiasi.conf /etc/nginx/sites-available/rentiasi.ro

# Activează site-ul
sudo ln -sf /etc/nginx/sites-available/rentiasi.ro /etc/nginx/sites-enabled/

# Dezactivează site-ul default
sudo rm -f /etc/nginx/sites-enabled/default

# Testare configurare
sudo nginx -t

# Obține certificate SSL cu Certbot
sudo certbot --nginx -d rentiasi.ro -d www.rentiasi.ro --non-interactive --agree-tos -m admin@rentiasi.ro

# Repornire Nginx
sudo systemctl restart nginx
sudo systemctl enable nginx

echo "✅ Nginx configurat cu SSL!"`;

  // Systemd service
  const systemdService = `# /etc/systemd/system/rentiasi.service
# Serviciu Systemd pentru Backend FastAPI

[Unit]
Description=Rentiasi.ro Backend API
Documentation=https://rentiasi.ro
After=network.target mongod.service
Wants=mongod.service

[Service]
Type=simple
User=www-data
Group=www-data
WorkingDirectory=/var/www/rentiasi/backend

# Mediul virtual Python
Environment="PATH=/var/www/rentiasi/venv/bin:/usr/local/bin:/usr/bin"
Environment="PYTHONUNBUFFERED=1"

# Comandă pornire
ExecStart=/var/www/rentiasi/venv/bin/uvicorn server:app --host 0.0.0.0 --port 8001 --workers 2

# Restart automat
Restart=always
RestartSec=10
StartLimitInterval=0

# Limite resurse
LimitNOFILE=65536

# Logging
StandardOutput=append:/var/www/rentiasi/logs/backend.log
StandardError=append:/var/www/rentiasi/logs/backend.error.log

[Install]
WantedBy=multi-user.target`;

  // Systemd setup script
  const systemdSetupScript = `#!/bin/bash
# ===========================================
# Configurare Serviciu Systemd
# ===========================================

# Setare permisiuni pentru www-data
sudo chown -R www-data:www-data /var/www/rentiasi
sudo chmod -R 755 /var/www/rentiasi

# Copiază serviciul
sudo cp rentiasi.service /etc/systemd/system/

# Reîncarcă systemd
sudo systemctl daemon-reload

# Activează serviciul la pornirea sistemului
sudo systemctl enable rentiasi

# Pornește serviciul
sudo systemctl start rentiasi

# Verifică status
sudo systemctl status rentiasi

echo ""
echo "✅ Serviciu configurat!"
echo "   Comenzi utile:"
echo "   - Verificare status: sudo systemctl status rentiasi"
echo "   - Oprire: sudo systemctl stop rentiasi"
echo "   - Repornire: sudo systemctl restart rentiasi"
echo "   - Vizualizare loguri: sudo journalctl -u rentiasi -f"`;

  // Script deploy/actualizare
  const deployScript = `#!/bin/bash
# ===========================================
# RENTIASI.RO - Script Deploy/Actualizare
# Rulează acest script pentru fiecare actualizare
# ===========================================

set -e
cd /var/www/rentiasi

echo "🚀 Începe deploy RENTIASI.RO..."
echo "================================"

# 1. Oprește serviciul backend
echo "⏹️  Oprire serviciu backend..."
sudo systemctl stop rentiasi || true

# 2. Backup baza de date
echo "💾 Creare backup bază de date..."
BACKUP_DIR="/var/backups/rentiasi"
mkdir -p $BACKUP_DIR
mongodump --db rentiasi --out "$BACKUP_DIR/pre-deploy-$(date +%Y%m%d-%H%M%S)"

# 3. Actualizare dependențe Backend
echo "🐍 Actualizare dependențe Python..."
source venv/bin/activate
cd backend
pip install -r requirements.txt --quiet

# 4. Actualizare și compilare Frontend
echo "⚛️  Compilare frontend..."
cd ../frontend
yarn install --silent
yarn build

# 5. Setare permisiuni corecte
echo "🔐 Setare permisiuni..."
sudo chown -R www-data:www-data /var/www/rentiasi
sudo chmod -R 755 /var/www/rentiasi

# 6. Pornire serviciu
echo "▶️  Pornire serviciu backend..."
sudo systemctl start rentiasi

# 7. Reîncărcare Nginx
echo "🔄 Reîncărcare Nginx..."
sudo nginx -t && sudo systemctl reload nginx

# 8. Verificare sănătate
echo "🏥 Verificare sănătate API..."
sleep 5
if curl -sf https://rentiasi.ro/api/health > /dev/null; then
    echo "✅ API funcționează corect!"
else
    echo "⚠️  API nu răspunde - verifică logurile!"
fi

echo ""
echo "================================"
echo "✅ Deploy finalizat cu succes!"
echo "🌐 Site: https://rentiasi.ro"
echo "📊 Status: sudo systemctl status rentiasi"
echo "📜 Loguri: sudo journalctl -u rentiasi -f"`;

  // Script backup
  const backupScript = `#!/bin/bash
# ===========================================
# RENTIASI.RO - Script Backup Automat
# Adaugă în crontab pentru backup zilnic
# ===========================================

BACKUP_DIR="/var/backups/rentiasi"
DATE=$(date +%Y%m%d-%H%M%S)
RETENTION_DAYS=30

# Creare director backup
mkdir -p "$BACKUP_DIR"

echo "🔄 Începe backup RENTIASI.RO - $DATE"

# 1. Backup MongoDB
echo "📦 Backup bază de date MongoDB..."
mongodump --db rentiasi --out "$BACKUP_DIR/db-$DATE" --quiet

# Comprimare backup DB
cd "$BACKUP_DIR"
tar -czf "db-$DATE.tar.gz" "db-$DATE"
rm -rf "db-$DATE"

# 2. Backup fișiere uploadate
echo "📁 Backup fișiere uploadate..."
if [ -d "/var/www/rentiasi/backend/uploads" ]; then
    tar -czf "$BACKUP_DIR/uploads-$DATE.tar.gz" -C /var/www/rentiasi/backend uploads 2>/dev/null || true
fi

# 3. Backup configurări
echo "⚙️  Backup configurări..."
tar -czf "$BACKUP_DIR/config-$DATE.tar.gz" \\
    /var/www/rentiasi/backend/.env \\
    /var/www/rentiasi/frontend/.env \\
    /etc/nginx/sites-available/rentiasi.ro \\
    /etc/systemd/system/rentiasi.service 2>/dev/null || true

# 4. Ștergere backup-uri vechi (> 30 zile)
echo "🗑️  Ștergere backup-uri vechi..."
find "$BACKUP_DIR" -name "*.tar.gz" -mtime +$RETENTION_DAYS -delete

# 5. Raport dimensiune
TOTAL_SIZE=$(du -sh "$BACKUP_DIR" | cut -f1)
echo ""
echo "✅ Backup completat!"
echo "   Locație: $BACKUP_DIR"
echo "   Dimensiune totală: $TOTAL_SIZE"
echo "   Păstrare: $RETENTION_DAYS zile"`;

  // Crontab setup
  const crontabSetup = `# Editează crontab: crontab -e
# Adaugă aceste linii pentru backup automat:

# Backup zilnic la ora 3:00 dimineața
0 3 * * * /var/www/rentiasi/scripts/backup.sh >> /var/log/rentiasi-backup.log 2>&1

# Reînnoire automată certificate SSL (lunar)
0 0 1 * * certbot renew --quiet && systemctl reload nginx

# Curățare loguri vechi (săptămânal)
0 0 * * 0 find /var/www/rentiasi/logs -name "*.log" -mtime +30 -delete`;

  // Requirements.txt
  const requirementsTxt = `# requirements.txt - Dependențe Python Backend
# Instalare: pip install -r requirements.txt

# Framework web
fastapi==0.109.0
uvicorn[standard]==0.27.0

# Bază de date MongoDB
motor==3.3.2

# Validare date
pydantic==2.5.3
pydantic-settings==2.1.0
email-validator==2.1.0

# Autentificare
python-jose[cryptography]==3.3.0
passlib[bcrypt]==1.7.4

# Upload fișiere
python-multipart==0.0.6
aiofiles==23.2.1

# HTTP client
httpx==0.26.0

# Procesare imagini
Pillow==10.2.0

# Procesare PDF
pypdf==3.17.4
weasyprint==60.2

# Alte utilități
python-dotenv==1.0.0

# Integrări externe (opțional)
# emergentintegrations>=0.1.0`;

  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} descărcat!`);
  };

  const downloadAllScripts = () => {
    const scripts = [
      { content: installScript, name: '01-install-dependencies.sh' },
      { content: structureScript, name: '02-create-structure.sh' },
      { content: backendScript, name: '03-setup-backend.sh' },
      { content: frontendScript, name: '04-setup-frontend.sh' },
      { content: nginxConfig, name: 'nginx-rentiasi.conf' },
      { content: nginxSetupScript, name: '05-setup-nginx.sh' },
      { content: systemdService, name: 'rentiasi.service' },
      { content: systemdSetupScript, name: '06-setup-systemd.sh' },
      { content: deployScript, name: 'deploy.sh' },
      { content: backupScript, name: 'backup.sh' },
      { content: requirementsTxt, name: 'requirements.txt' }
    ];

    scripts.forEach(({ content, name }, index) => {
      setTimeout(() => downloadFile(content, name), index * 200);
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-8 max-w-5xl">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-serif flex items-center gap-3">
              <Server className="text-primary" />
              Instalare VPS Linux
            </h1>
            <p className="text-muted-foreground mt-2">
              Documentație completă pentru deploy pe server Ubuntu/Debian
            </p>
          </div>
          <Button onClick={downloadAllScripts} className="bg-primary">
            <Download size={18} className="mr-2" />
            Descarcă Toate Scripturile
          </Button>
        </div>

        {/* Cerințe Hardware */}
        <div className="glass p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <HardDrive className="text-primary" />
            Cerințe Minime Server
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <Cpu className="mx-auto mb-2 text-blue-400" size={24} />
              <p className="text-sm font-medium">2 vCPU</p>
              <p className="text-xs text-muted-foreground">Procesoare</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <MemoryStick className="mx-auto mb-2 text-green-400" size={24} />
              <p className="text-sm font-medium">4 GB RAM</p>
              <p className="text-xs text-muted-foreground">Memorie</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <HardDrive className="mx-auto mb-2 text-yellow-400" size={24} />
              <p className="text-sm font-medium">40 GB SSD</p>
              <p className="text-xs text-muted-foreground">Stocare</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <Globe className="mx-auto mb-2 text-purple-400" size={24} />
              <p className="text-sm font-medium">IP Public</p>
              <p className="text-xs text-muted-foreground">+ Domeniu</p>
            </div>
          </div>
        </div>

        {/* Cerințe Software */}
        <div className="glass p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Package className="text-primary" />
            Software Necesar
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <Server className="mx-auto mb-2 text-orange-400" size={24} />
              <p className="text-sm font-medium">Ubuntu 22.04</p>
              <p className="text-xs text-muted-foreground">sau Debian 12</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <Database className="mx-auto mb-2 text-green-400" size={24} />
              <p className="text-sm font-medium">MongoDB 7.0</p>
              <p className="text-xs text-muted-foreground">Bază de date</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <FileCode className="mx-auto mb-2 text-yellow-400" size={24} />
              <p className="text-sm font-medium">Python 3.11</p>
              <p className="text-xs text-muted-foreground">Backend FastAPI</p>
            </div>
            <div className="bg-slate-800/50 p-4 rounded-lg text-center">
              <Globe className="mx-auto mb-2 text-blue-400" size={24} />
              <p className="text-sm font-medium">Node.js 20</p>
              <p className="text-xs text-muted-foreground">Frontend React</p>
            </div>
          </div>
        </div>

        {/* Pasul 1 */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</span>
              Instalare Dependențe Sistem
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(installScript, '01-install-dependencies.sh')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <p className="text-muted-foreground mb-4">
            Conectează-te la server prin SSH și rulează acest script pentru a instala toate dependențele necesare.
          </p>
          <CodeBlock code={installScript} section="install" />
        </div>

        {/* Pasul 2 */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</span>
              Creare Structură Directoare
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(structureScript, '02-create-structure.sh')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <CodeBlock code={structureScript} section="structure" />
        </div>

        {/* Pasul 3 */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</span>
              Configurare Backend
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(backendScript, '03-setup-backend.sh')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
            <p className="text-sm text-yellow-400">
              <strong>⚠️ IMPORTANT:</strong> Înainte de a rula acest script, copiază fișierele <code>server.py</code> și <code>requirements.txt</code> în directorul <code>/var/www/rentiasi/backend/</code>
            </p>
          </div>
          <CodeBlock code={backendScript} section="backend" />
        </div>

        {/* Pasul 4 */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">4</span>
              Configurare Frontend
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(frontendScript, '04-setup-frontend.sh')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg mb-4">
            <p className="text-sm text-yellow-400">
              <strong>⚠️ IMPORTANT:</strong> Copiază toate fișierele din folderul <code>frontend/</code> în <code>/var/www/rentiasi/frontend/</code>
            </p>
          </div>
          <CodeBlock code={frontendScript} section="frontend" />
        </div>

        {/* Pasul 5 - Nginx */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">5</span>
              Configurare Nginx + SSL
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadFile(nginxConfig, 'nginx-rentiasi.conf')}>
                <Download size={14} className="mr-1" /> Config
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadFile(nginxSetupScript, '05-setup-nginx.sh')}>
                <Download size={14} className="mr-1" /> Script
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground mb-4">Configurare reverse proxy cu certificate SSL gratuite de la Let's Encrypt.</p>
          <CodeBlock code={nginxConfig} section="nginx" />
          <div className="mt-4">
            <p className="text-sm text-muted-foreground mb-2">Script activare:</p>
            <CodeBlock code={nginxSetupScript} section="nginx-setup" />
          </div>
        </div>

        {/* Pasul 6 - Systemd */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <span className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">6</span>
              Serviciu Systemd (Auto-start)
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => downloadFile(systemdService, 'rentiasi.service')}>
                <Download size={14} className="mr-1" /> Service
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadFile(systemdSetupScript, '06-setup-systemd.sh')}>
                <Download size={14} className="mr-1" /> Script
              </Button>
            </div>
          </div>
          <p className="text-muted-foreground mb-4">Backend-ul va porni automat la restart server.</p>
          <CodeBlock code={systemdService} section="systemd" />
          <div className="mt-4">
            <p className="text-sm text-muted-foreground mb-2">Script activare:</p>
            <CodeBlock code={systemdSetupScript} section="systemd-setup" />
          </div>
        </div>

        {/* Script Deploy */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <RefreshCw className="text-green-400" />
              Script Actualizare (Deploy)
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(deployScript, 'deploy.sh')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <p className="text-muted-foreground mb-4">
            Rulează acest script de fiecare dată când actualizezi aplicația.
          </p>
          <CodeBlock code={deployScript} section="deploy" />
        </div>

        {/* Script Backup */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Shield className="text-blue-400" />
              Script Backup Automat
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(backupScript, 'backup.sh')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <CodeBlock code={backupScript} section="backup" />
          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <p className="text-sm text-blue-400 font-medium mb-2">📅 Programare backup automat (crontab):</p>
            <CodeBlock code={crontabSetup} section="crontab" />
          </div>
        </div>

        {/* Requirements.txt */}
        <div className="glass p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Package className="text-purple-400" />
              requirements.txt
            </h2>
            <Button variant="outline" size="sm" onClick={() => downloadFile(requirementsTxt, 'requirements.txt')}>
              <Download size={14} className="mr-1" /> Download
            </Button>
          </div>
          <CodeBlock code={requirementsTxt} section="requirements" />
        </div>

        {/* Module Aplicație */}
        <div className="glass p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Package className="text-primary" />
            Module Aplicație RENTIASI.RO
          </h2>
          <p className="text-muted-foreground mb-4">
            Aplicația include următoarele module funcționale:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-blue-400 mb-2">🏠 Gestiune Apartamente</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Adăugare/editare/ștergere apartamente</li>
                <li>• Galerie imagini cu upload în MongoDB</li>
                <li>• Traduceri automate RO/EN/FR</li>
                <li>• Hartă interactivă OpenStreetMap</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-green-400 mb-2">📋 Contracte</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Gestiune chiriași și proprietari</li>
                <li>• Generare contracte PDF</li>
                <li>• Template-uri multilingve</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-yellow-400 mb-2">⏰ Work In/Out (Pontaj)</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Înregistrare ore lucrate</li>
                <li>• Tarifare pe zile (weekday/weekend)</li>
                <li>• Opțiune "Service Day" cu preț fix</li>
                <li>• Acces rapid mobil prin link secret</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-purple-400 mb-2">🧾 Facturi</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Generare automată din pontaj</li>
                <li>• Numerotare secvențială automată</li>
                <li>• Export PDF și trimitere email</li>
                <li>• Gestiune clienți</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg border border-orange-500/30">
              <h3 className="font-semibold text-orange-400 mb-2">🔧 Fișe Service Auto/Moto</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• CRUD complet fișe service vehicule</li>
                <li>• <strong className="text-white">CRUD Clienți Service</strong> (salvare, editare, ștergere, selectare rapidă)</li>
                <li>• Multilingv: EN (UK), IT (Italia), RO</li>
                <li>• TVA automat per țară (20%/22%/19%)</li>
                <li>• Valută per țară (£/€/Lei)</li>
                <li>• Ștampile: SERVICED / CHECK / VERIFICAT</li>
                <li>• <strong className="text-white">Ștampilă firmă opțională</strong> (checkbox, doar la printare)</li>
                <li>• Opțiune ascundere prețuri la printare</li>
                <li>• <strong className="text-white">QR Code verificare autenticitate</strong></li>
                <li>• Text QR tradus: "Scanează pentru verificare"</li>
                <li>• Pagină publică verificare (fără ștampilă, fără header/footer)</li>
                <li>• Banner "AUTENTIC" tradus per limbă</li>
                <li>• <strong className="text-white">Numerotare secvențială automată</strong></li>
                <li>• <strong className="text-white">Butoane: Preview | Print | Edit | Delete</strong></li>
                <li>• Print optimizat A4 (1 pagină)</li>
                <li>• <strong className="text-white">Inclus în backup/restore v2.2</strong></li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-pink-400 mb-2">✉️ Email & Newsletter</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Configurare IMAP/SMTP separată</li>
                <li>• Trimitere newsletter abonați</li>
                <li>• Notificări formular contact</li>
                <li>• Import abonați din CSV</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-cyan-400 mb-2">🎨 Personalizare Tema</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Culori site-wide (text, background)</li>
                <li>• Transparență elemente</li>
                <li>• Upload imagine cover</li>
                <li>• Mod mentenanță</li>
              </ul>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-red-400 mb-2">🔍 SEO</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Titlu și meta descriere</li>
                <li>• robots.txt dinamic</li>
                <li>• Sitemap.xml automat</li>
                <li>• Open Graph tags</li>
              </ul>
            </div>
          </div>
        </div>

        {/* MongoDB Collections */}
        <div className="glass p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Database className="text-green-400" />
            Colecții MongoDB (Backup v2.2)
          </h2>
          <p className="text-muted-foreground mb-4">
            Backup-ul include automat toate aceste colecții:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-semibold text-blue-400 mb-2">📦 Conținut</h4>
              <ul className="text-muted-foreground space-y-1">
                <li>• apartments</li>
                <li>• categories</li>
                <li>• images</li>
                <li>• collaborators</li>
              </ul>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-semibold text-yellow-400 mb-2">💼 Facturi & Pontaj</h4>
              <ul className="text-muted-foreground space-y-1">
                <li>• time_entries</li>
                <li>• invoices</li>
                <li>• clients</li>
                <li>• pricing_settings</li>
                <li>• invoice_settings</li>
              </ul>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-semibold text-green-400 mb-2">📋 Contracte</h4>
              <ul className="text-muted-foreground space-y-1">
                <li>• contracts</li>
                <li>• contract_tenants</li>
                <li>• contract_owners</li>
                <li>• contract_template</li>
              </ul>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg border border-orange-500/30">
              <h4 className="font-semibold text-orange-400 mb-2">🔧 Fișe Service (NOU)</h4>
              <ul className="text-muted-foreground space-y-1">
                <li>• <strong className="text-white">service_sheets</strong></li>
                <li>• <strong className="text-white">service_clients</strong></li>
                <li>• <strong className="text-white">service_providers</strong></li>
                <li>• <strong className="text-white">service_settings</strong></li>
              </ul>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-semibold text-pink-400 mb-2">✉️ Comunicare</h4>
              <ul className="text-muted-foreground space-y-1">
                <li>• newsletter_subscribers</li>
                <li>• contact_messages</li>
                <li>• email_settings</li>
              </ul>
            </div>
            <div className="p-3 bg-slate-800/50 rounded-lg">
              <h4 className="font-semibold text-cyan-400 mb-2">⚙️ Setări</h4>
              <ul className="text-muted-foreground space-y-1">
                <li>• global_settings</li>
                <li>• about_content</li>
                <li>• contact_settings</li>
                <li>• policies</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Structura FTP */}
        <div className="glass p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileCode className="text-primary" />
            Structura Fișiere pe Server
          </h2>
          <pre className="bg-slate-950 p-4 rounded-lg text-sm text-gray-300 font-mono overflow-x-auto">
{`/var/www/rentiasi/
├── venv/                    # Mediu virtual Python
│
├── backend/
│   ├── server.py            # Aplicația FastAPI principală
│   ├── requirements.txt     # Dependențe Python
│   ├── .env                 # Variabile de mediu (parole, chei)
│   └── uploads/             # Imagini încărcate
│
├── frontend/
│   ├── build/               # Fișiere React compilate (producție)
│   ├── src/                 # Cod sursă React
│   ├── public/              # Fișiere publice (favicon, etc.)
│   ├── package.json         # Dependențe Node.js
│   └── .env                 # URL backend
│
├── scripts/
│   ├── deploy.sh            # Script actualizare
│   └── backup.sh            # Script backup
│
└── logs/
    ├── backend.log          # Loguri aplicație
    └── backend.error.log    # Erori aplicație

/etc/nginx/sites-available/
└── rentiasi.ro              # Configurare Nginx

/etc/systemd/system/
└── rentiasi.service         # Serviciu systemd

/var/backups/rentiasi/       # Backup-uri automate`}
          </pre>
        </div>

        {/* Troubleshooting */}
        <div className="glass p-6">
          <h2 className="text-xl font-semibold mb-4 text-red-400">🔧 Depanare Probleme</h2>
          <div className="space-y-4 text-sm">
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <p className="font-medium text-yellow-400 mb-2">❌ Backend nu pornește?</p>
              <pre className="text-xs text-gray-400">{`# Verifică loguri detaliate
sudo journalctl -u rentiasi -f --no-pager

# Verifică dacă portul 8001 e liber
sudo lsof -i :8001

# Testează manual
cd /var/www/rentiasi && source venv/bin/activate
cd backend && python3 -m uvicorn server:app --host 0.0.0.0 --port 8001`}</pre>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <p className="font-medium text-yellow-400 mb-2">❌ Eroare 502 Bad Gateway?</p>
              <pre className="text-xs text-gray-400">{`# Verifică dacă backend-ul rulează
curl http://localhost:8001/api/health

# Verifică configurare Nginx
sudo nginx -t

# Repornește serviciile
sudo systemctl restart rentiasi nginx`}</pre>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <p className="font-medium text-yellow-400 mb-2">❌ MongoDB nu funcționează?</p>
              <pre className="text-xs text-gray-400">{`# Verifică status
sudo systemctl status mongod

# Verifică loguri MongoDB
sudo tail -50 /var/log/mongodb/mongod.log

# Repornire
sudo systemctl restart mongod`}</pre>
            </div>
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <p className="font-medium text-yellow-400 mb-2">❌ Erori permisiuni?</p>
              <pre className="text-xs text-gray-400">{`# Resetare permisiuni
sudo chown -R www-data:www-data /var/www/rentiasi
sudo chmod -R 755 /var/www/rentiasi
sudo chmod 600 /var/www/rentiasi/backend/.env`}</pre>
            </div>
          </div>
        </div>

        {/* Comenzi utile */}
        <div className="glass p-6 bg-primary/5 border-primary/20">
          <h2 className="text-xl font-semibold mb-4">📋 Comenzi Utile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-mono">
            <div>
              <p className="text-primary mb-1"># Serviciu Backend</p>
              <p className="text-gray-400">sudo systemctl status rentiasi</p>
              <p className="text-gray-400">sudo systemctl restart rentiasi</p>
              <p className="text-gray-400">sudo journalctl -u rentiasi -f</p>
            </div>
            <div>
              <p className="text-primary mb-1"># Nginx</p>
              <p className="text-gray-400">sudo nginx -t</p>
              <p className="text-gray-400">sudo systemctl reload nginx</p>
              <p className="text-gray-400">sudo tail -f /var/log/nginx/rentiasi.error.log</p>
            </div>
            <div>
              <p className="text-primary mb-1"># MongoDB</p>
              <p className="text-gray-400">sudo systemctl status mongod</p>
              <p className="text-gray-400">mongosh rentiasi</p>
              <p className="text-gray-400">mongodump --db rentiasi --out backup/</p>
            </div>
            <div>
              <p className="text-primary mb-1"># SSL Certificate</p>
              <p className="text-gray-400">sudo certbot certificates</p>
              <p className="text-gray-400">sudo certbot renew --dry-run</p>
            </div>
          </div>
        </div>

        {/* Cerințe Livrare Proiect */}
        <div className="glass p-6 border-2 border-orange-500/30 bg-orange-500/5">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <span className="text-2xl">📦</span>
            Cerințe Livrare Proiect (Versiune Stabilă)
          </h2>
          <p className="text-muted-foreground mb-4">
            Pentru ca instalarea și rularea proiectului să fie <strong className="text-white">predictibile, rapide și fără conflicte</strong>, 
            proiectul trebuie livrat într-o versiune stabilă și modernă care să includă:
          </p>
          
          <div className="space-y-4">
            {/* Frontend Modern */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                <span>⚡</span> Frontend Modern
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• Construit exclusiv cu <strong className="text-white">Vite</strong> sau <strong className="text-white">Next.js</strong></li>
                <li>• <span className="text-red-400">Fără CRACO</span>, <span className="text-red-400">fără react-scripts</span></li>
                <li>• <span className="text-red-400">Fără pachete depășite</span> precum <code className="bg-slate-700 px-1 rounded">ajv-keywords</code> sau <code className="bg-slate-700 px-1 rounded">workbox</code></li>
                <li>• Dependințe actualizate și compatibile</li>
              </ul>
            </div>

            {/* Configurare */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-green-400 mb-2 flex items-center gap-2">
                <span>⚙️</span> Fișiere de Configurare
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• <code className="bg-slate-700 px-1 rounded">.env.example</code> complet pentru <strong className="text-white">backend</strong></li>
                <li>• <code className="bg-slate-700 px-1 rounded">.env.example</code> complet pentru <strong className="text-white">frontend</strong></li>
                <li>• <code className="bg-slate-700 px-1 rounded">nginx.conf.example</code> cu configurare completă</li>
              </ul>
            </div>

            {/* Script Automat */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-yellow-400 mb-2 flex items-center gap-2">
                <span>🚀</span> Script Automat de Instalare
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• Configurare automată <strong className="text-white">Node 18+</strong></li>
                <li>• Instalare dependințe (npm/yarn)</li>
                <li>• Build automat frontend</li>
                <li>• Pregătire backend (venv, pip install)</li>
                <li>• Configurare servicii systemd</li>
              </ul>
            </div>

            {/* Structura Proiect */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-purple-400 mb-2 flex items-center gap-2">
                <span>📁</span> Structură Clară de Directoare
              </h3>
              <pre className="text-xs text-gray-400 ml-6 mt-2">{`/var/www/rentiasi/
├── backend/
│   ├── server.py          # FastAPI entrypoint (app)
│   ├── requirements.txt
│   ├── .env               # Configurare backend
│   └── venv/              # Virtual environment
├── frontend/
│   ├── dist/              # Build-ul frontend (static)
│   └── .env.example
├── nginx.conf.example
├── install.sh             # Script automat instalare
└── README.md              # Documentație`}</pre>
            </div>

            {/* Backend Decuplat */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-cyan-400 mb-2 flex items-center gap-2">
                <span>🔌</span> Backend Complet Decuplat
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• Backend servește <strong className="text-white">doar API-ul</strong> (fără frontend)</li>
                <li>• Frontend servit de <strong className="text-white">Nginx</strong> ca fișiere statice</li>
                <li>• Nginx face proxy către API pe <code className="bg-slate-700 px-1 rounded">/api/*</code></li>
                <li>• Separare completă între servicii</li>
              </ul>
            </div>

            {/* Documentație */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-pink-400 mb-2 flex items-center gap-2">
                <span>📖</span> Documentație Completă
              </h3>
              <ul className="text-sm text-muted-foreground space-y-1 ml-6">
                <li>• <strong className="text-white">Documentație de instalare în 10 pași simpli</strong></li>
                <li>• Documentație completă a <strong className="text-white">endpoint-urilor API</strong></li>
                <li>• Ghid troubleshooting pentru probleme comune</li>
                <li>• Exemple de comenzi pentru management</li>
              </ul>
            </div>

            {/* Sistemd Service */}
            <div className="p-4 bg-slate-800/50 rounded-lg">
              <h3 className="font-semibold text-orange-400 mb-2 flex items-center gap-2">
                <span>🔧</span> Configurare Systemd (Backend)
              </h3>
              <pre className="text-xs text-gray-400 ml-2 mt-2">{`# /etc/systemd/system/rentiasi-backend.service

[Unit]
Description=Rentiasi Backend API
After=network.target

[Service]
User=www-data
Group=www-data
WorkingDirectory=/var/www/rentiasi/backend
Environment="PATH=/var/www/rentiasi/backend/venv/bin"
ExecStart=/var/www/rentiasi/backend/venv/bin/uvicorn server:app --host 0.0.0.0 --port 8001
Restart=always

[Install]
WantedBy=multi-user.target`}</pre>
              <p className="text-xs text-muted-foreground mt-3 ml-2">
                <strong className="text-white">Notă:</strong> Fișierul principal este <code className="bg-slate-700 px-1 rounded">server.py</code> 
                și obiectul FastAPI este <code className="bg-slate-700 px-1 rounded">app</code> 
                → <code className="bg-slate-700 px-1 rounded">server:app</code>
              </p>
            </div>
          </div>

          {/* Summary Box */}
          <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-green-400 font-medium text-center">
              ✅ Toate aceste cerințe asigură o instalare predictibilă, rapidă și fără conflicte!
            </p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDeploymentPage;
